/* OpenMic.jsx -- Harvest Open Mic Night performer sign-up (/#openmic).

   THE ONLY PAGE ON THIS SITE WHOSE WHOLE EXISTENCE IS DATED. Everything else
   here outlives a season; this is one night. So the page reads its details
   from window.PPR_OPEN_MIC in data.js -- the same object the home-page popup
   (OpenMicNotice in App.jsx) and the footer link (Shell.jsx) read -- and moving
   the night is a one-line edit in one file, not three.

   IT RETIRES ITSELF, through the same window.pprNoticeIsLive guard as every
   timed popup, and for the harder of the two reasons. A retired popup is a
   modal nobody sees. A form that outlives its event is worse: it keeps
   accepting acts for a night that already happened, tells each person the
   office will be in touch about their slot, and nothing about that is visible
   to the coordinator except rows arriving in November. Past closesAt the form
   is replaced by a plain "this one is over" panel that still names the office,
   so a late arrival lands somewhere honest instead of on a 404.
   THE GUARD FAILS CLOSED -- an unparseable date hides the form rather than
   pinning it open forever. See pprNoticeIsLive in data.js; that direction is
   load-bearing and scripts/notice-retirement.test.mjs holds it.

   NOTHING ON THE SERVER ENFORCES THAT DATE, on purpose. api/intake.js takes an
   open-mic row whenever one arrives. A second copy of the date on the server is
   a second thing to move when the night moves, and the failure it would cause --
   a performer who filled the whole form in and got a 400 back -- is worse than
   the one it prevents, which is a stray row the coordinator can see and delete.

   ACT TYPE AND LENGTH ARE THE POINT OF THE FORM. The coordinator is not
   reading an inbox, they are building a running order: forty acts into two
   hours, and a night that is all singers back to back is a worse night than
   one that alternates. Those two fields are what that decision is made from,
   which is why they are required and why the dashboard tab and the CSV export
   both lead with them. */

// THE FLYER'S OWN LIST, IN THE FLYER'S OWN WORDS AND ORDER. It reads
// "Musicians · Singers · Rappers · Poets · Dancers · Comedians", and somebody
// filling this in has that poster in front of them, so a list that quietly
// renamed rap to "spoken word" would read as a different event. An earlier
// draft did exactly that.
//
// Visual artists were on the first cut of the flyer and came off it (J.J.,
// 2026-09-22); img/harvest-open-mic-*.jpg are re-cut from the corrected
// poster. This list, the poster and the server allowlist move together or not
// at all -- OPEN_MIC_ACT_TYPES in api/intake.js allowlists the same strings
// and silently falls back to "Something else", which turns a mismatch into a
// column of shrugs nobody can sort a night by.
const OPEN_MIC_ACT_TYPES = [
  "Musician",
  "Singer",
  "Rapper",
  "Poet",
  "Dancer",
  "Comedian",
  "Something else",
];

// Kept short and generous. An open mic slot is five minutes; asking someone to
// type a number invites "10-12 min?" and a coordinator doing arithmetic.
const OPEN_MIC_DURATIONS = ["Under 3 minutes", "3–5 minutes", "5–8 minutes", "Not sure yet"];

// TWO OPTIONS, NOT THREE. The flyer says AGES 13+, so there is no "Under 13"
// here and the copy does not say "all ages" -- an under-13 who filled this in
// would be turned away at the door having been told on the way in that they
// were welcome. What the field is actually for is the 13-17 half: they need a
// guardian to sign them in on the night, and the office has to know how many.
const OPEN_MIC_AGE_GROUPS = ["13–17", "18+"];

/* ---------- harvest decoration ----------
   Drawn here rather than cut from the flyer, because the poster's pumpkins sit
   on its own sunset gradient and cutting them out cleanly means masking a
   soft-edged raster against a dark sky. These are vector, so they stay crisp
   at any size, weigh nothing, recolour with the page and satisfy the house
   icon standard -- no emoji, no glyphs.

   The palette and the construction are taken FROM the poster: five overlapping
   lobes, darkest at the edges, lightest down the middle, a single highlight
   near the top left, and a short olive stem with a notch. Matching it matters
   because the person filling this form in has the poster in front of them. */

const PUMPKIN_TONES = {
  orange: { dark: "#C9541F", mid: "#E2742C", light: "#F2913F", line: "#A8441A", shine: "#F7B169" },
  teal:   { dark: "#2C6A61", mid: "#3E877B", light: "#63AC9E", line: "#1F4F49", shine: "#8FC9BC" },
  cream:  { dark: "#DCCEAC", mid: "#EADFC3", light: "#F8F2E0", line: "#9C8A5E", shine: "#FFFBF0" },
};

function HarvestPumpkin({ tone = "orange", width = 120, style }) {
  const c = PUMPKIN_TONES[tone] || PUMPKIN_TONES.orange;
  return (
    <svg
      viewBox="0 0 100 86"
      width={width}
      aria-hidden="true"
      focusable="false"
      style={{ display: "block", flexShrink: 0, ...style }}
    >
      <path
        d="M50 26 c0-9 3-15 9-18 c4 4 3 9-1 12 c-3 3-4 5-3 8z"
        fill="#5E7A35" stroke="#465E26" strokeWidth="1.6" strokeLinejoin="round"
      />
      <ellipse cx="26" cy="54" rx="20" ry="28" fill={c.dark}  stroke={c.line} strokeWidth="2" />
      <ellipse cx="74" cy="54" rx="20" ry="28" fill={c.dark}  stroke={c.line} strokeWidth="2" />
      <ellipse cx="37" cy="54" rx="21" ry="30" fill={c.mid}   stroke={c.line} strokeWidth="2" />
      <ellipse cx="63" cy="54" rx="21" ry="30" fill={c.mid}   stroke={c.line} strokeWidth="2" />
      <ellipse cx="50" cy="54" rx="17" ry="31" fill={c.light} stroke={c.line} strokeWidth="2" />
      <ellipse cx="44" cy="42" rx="3.4" ry="5.6" fill={c.shine} opacity="0.85" />
    </svg>
  );
}

// The patch, as a band that sits ON a boundary. The pumpkins are pushed down
// past the edge so they are cropped by it rather than floating above it --
// same read as the poster, where the row straddles the horizon.
//
// Deliberately WIDER than any viewport and centred, so the row never ends
// mid-screen with a gap after it; the ends simply run off both sides.
const PUMPKIN_PATCH = [
  { tone: "cream",  w: 78 },
  { tone: "orange", w: 132 },
  { tone: "teal",   w: 66 },
  { tone: "orange", w: 108 },
  { tone: "cream",  w: 60 },
  { tone: "orange", w: 150 },
  { tone: "teal",   w: 84 },
  { tone: "orange", w: 116 },
  { tone: "cream",  w: 72 },
  { tone: "orange", w: 126 },
  { tone: "teal",   w: 62 },
  { tone: "orange", w: 96 },
];

function PumpkinPatch({ drop = 18, style }) {
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: -6,
        pointerEvents: "none",
        marginBottom: -drop,
        ...style,
      }}
    >
      {PUMPKIN_PATCH.map((p, i) => (
        <HarvestPumpkin
          key={i}
          tone={p.tone}
          width={p.w}
          style={{ marginLeft: i ? -Math.round(p.w * 0.18) : 0 }}
        />
      ))}
    </div>
  );
}

// The poster's night, rebuilt as a page header: sky gradient into sunset, the
// moon behind the type, a scatter of stars, and the patch along the bottom.
// Every colour is lifted from the flyer so the page and the paper read as one
// thing.
//
// The stars are a FIXED list, not random, because a random scatter regenerates
// on every render and a star that moves when you tab a field is a distraction
// nobody asked for.
const OPEN_MIC_STARS = [
  [6, 26, 2.0], [13, 58, 1.4], [19, 18, 1.7], [27, 70, 1.2], [33, 34, 1.5],
  [41, 12, 1.9], [48, 62, 1.3], [56, 24, 1.6], [63, 48, 1.2], [71, 16, 2.0],
  [78, 66, 1.4], [85, 30, 1.7], [92, 54, 1.3], [96, 20, 1.5],
];

function OpenMicHero({ eyebrow, title, body }) {
  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        borderBottom: "2px solid var(--c-ink)",
        background:
          "linear-gradient(180deg, #2A1245 0%, #3B1F4A 38%, #6B3352 68%, #B45C44 88%, #D8763F 100%)",
        padding: "80px 0 0",
      }}
    >
      {/* Moon: one soft radial behind the type, the way the poster puts it
          behind the microphone. Low opacity so the title stays the brightest
          thing on the panel. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "50%",
          top: 40,
          width: 420,
          height: 420,
          transform: "translateX(-50%)",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,231,164,0.55) 0%, rgba(255,214,132,0.22) 45%, rgba(255,200,120,0) 70%)",
          pointerEvents: "none",
        }}
      />
      {/* Plain positioned dots, not an SVG. A viewBox stretched to the panel's
          aspect with preserveAspectRatio="none" squashes every circle into a
          dash, which is what the first version shipped -- the stars came out as
          little horizontal ticks. Percentage-positioned divs stay round at any
          width. */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {OPEN_MIC_STARS.map(([x, y, r], i) => (
          <span
            key={i}
            style={{
              position: "absolute",
              left: `${x}%`,
              top: `${y}%`,
              width: r * 1.6,
              height: r * 1.6,
              borderRadius: "50%",
              background: "#FFF1D6",
              opacity: 0.55,
            }}
          />
        ))}
      </div>

      <div className="container" style={{ position: "relative" }}>
        <div className="eyebrow" style={{ color: "#FFCF73" }}>{eyebrow}</div>
        <h1
          className="display"
          style={{
            fontSize: "clamp(44px, 7.5vw, 96px)",
            margin: "12px 0 18px",
            lineHeight: 0.98,
            color: "#FFF1D6",
          }}
        >
          {title}
        </h1>
        <p style={{ fontSize: 17, maxWidth: 700, lineHeight: 1.55, color: "#F6E3D2", marginBottom: 34 }}>
          {body}
        </p>
      </div>

      <PumpkinPatch drop={22} />
    </section>
  );
}

function OpenMicPage() {
  const cfg = window.PPR_OPEN_MIC || {};
  const live = window.pprNoticeIsLive ? window.pprNoticeIsLive(cfg.closesAt, Date.now()) : false;

  const blank = {
    name: "", email: "", phone: "", ageGroup: "",
    actType: "", actTitle: "", duration: "", performers: "",
    needs: "", notes: "",
  };
  const [data, setData] = React.useState(blank);
  const [submitted, setSubmitted] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const update = (k, v) => setData(d => ({ ...d, [k]: v }));

  // THE SELECTS ARE NOT BROWSER-VALIDATED AND THAT IS NOT AN OVERSIGHT HERE.
  // The shared Field (Camps.jsx) forwards `required` to <input> only, never to
  // <select>, so marking a dropdown required styles the label with an asterisk
  // and enforces nothing. Five other forms on this site rely on that component,
  // so the fix is not to widen it under them -- it is to check here. Act type
  // and length are the two fields the running order is built from, and the
  // server's fallback for a missing act type is "Something else", which is a
  // silent downgrade of the one answer the coordinator needed.
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!data.ageGroup || !data.actType || !data.duration) {
      setError("Pick an age group, a type of act and how long you need — those three are what the running order is built from.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/intake", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "open-mic", ...data }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || `error_${res.status}`);
      setSubmitted({ ref: json.ref });
    } catch (err) {
      setError("Couldn't send that just now. Try again, or email PPRperformingarts@phila.gov.");
    } finally {
      setSubmitting(false);
    }
  };

  const hero = (
    <OpenMicHero
      eyebrow="Open mic"
      title={cfg.name || "Open Mic Night"}
      body={
        !live
          ? "This one is over. Thanks to everyone who got up."
          : cfg.signupsClosed
            ? "A night of music, singing, poetry, dance and comedy from your neighbors. Sign-ups are closed, but the night is still on and everyone is welcome to come and watch."
            : "Musicians, singers, rappers, poets, dancers, comedians — bring five minutes of whatever you do. Ages 13 and up, and all are welcome. Sign up below and the Performing Arts Office will send you a slot and a call time."
      }
    />
  );

  // Sign-ups closed while the night is still on (signupsClosed in data.js):
  // no form, just the details and an invitation to come. Checked before the
  // live form below and after the !live "this one is over" panel.
  if (live && cfg.signupsClosed) {
    return (
      <div>
        <style>{`
          .openmic-intro { display: grid; grid-template-columns: 1fr auto; gap: 28px; align-items: center; }
          @media (max-width: 640px) { .openmic-intro { grid-template-columns: 1fr; justify-items: start; } }
        `}</style>
        {hero}
        <section className="section">
          <div className="container" style={{ maxWidth: 920 }}>
            <div className="card openmic-intro" style={{ background: "var(--c-cream-deep)", marginBottom: 20 }}>
              <OpenMicFacts cfg={cfg} />
              {cfg.flyer && (
                <img
                  src={cfg.flyer}
                  alt={`${cfg.name || "Open Mic Night"} flyer. ${[cfg.date, cfg.time, cfg.venueName].filter(Boolean).join(", ")}. Musicians, singers, rappers, poets, dancers, comedians. ${cfg.audience || ""}`.trim()}
                  loading="lazy"
                  style={{
                    display: "block",
                    width: "100%",
                    maxWidth: 200,
                    height: "auto",
                    borderRadius: 10,
                    border: "2px solid var(--c-ink)",
                    justifySelf: "end",
                  }}
                />
              )}
            </div>
            <div className="card" style={{ background: "var(--c-cream-deep)" }}>
              <div className="display" style={{ fontSize: 24, marginBottom: 10 }}>Come on out.</div>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: "var(--c-ink-soft)", margin: 0 }}>
                Sign-ups for {cfg.name || "the open mic"} are closed, but the night is still on and
                everyone is welcome to come and watch. Bring a friend. Questions? Email{" "}
                <a href="mailto:PPRperformingarts@phila.gov">PPRperformingarts@phila.gov</a> or call
                215-685-3585.
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!live) {
    return (
      <div>
        {hero}
        <section className="section">
          <div className="container" style={{ maxWidth: 720 }}>
            <div className="card" style={{ background: "var(--c-cream-deep)" }}>
              <div className="display" style={{ fontSize: 26, marginBottom: 10 }}>Sign-ups are closed.</div>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: "var(--c-ink-soft)", margin: 0 }}>
                {cfg.name || "The open mic"} has happened and this form is no longer taking acts.
                The Performing Arts Office runs events like this through the year &mdash; email{" "}
                <a href="mailto:PPRperformingarts@phila.gov">PPRperformingarts@phila.gov</a> or call
                215-685-3585 to hear about the next one.
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (submitted) {
    return (
      <div>
        {hero}
        <section className="section">
          <div className="container" style={{ maxWidth: 720 }}>
            <div className="card" style={{ background: "var(--c-cream-deep)" }}>
              <div className="display" style={{ fontSize: "clamp(30px, 5vw, 46px)", lineHeight: 1.05, marginBottom: 14 }}>
                You&apos;re on the list.
              </div>
              <p style={{ fontSize: 16.5, lineHeight: 1.6, marginTop: 0 }}>
                The Performing Arts Office has your act and will email you a slot and a call time.
                A confirmation is on its way to your inbox now. Reference {submitted.ref}.
              </p>
              <OpenMicFacts cfg={cfg} />
              <button
                type="button"
                className="btn secondary"
                style={{ marginTop: 22 }}
                onClick={() => { setSubmitted(null); setData(blank); }}
              >
                Sign up someone else
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        .openmic-intro {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 28px;
          align-items: center;
        }
        @media (max-width: 640px) {
          .openmic-intro { grid-template-columns: 1fr; justify-items: start; }
        }
      `}</style>
      {hero}
      <section className="section">
        <div className="container" style={{ maxWidth: 920 }}>
          {/* THE POSTER, BESIDE THE FACTS. The flyer's own call to action is
              "SIGN UP AT PHILLYPERFORMS.COM" with no path, so most people
              reaching this form got here from that piece of paper and nothing
              else. Showing it is how they know they are in the right place.
              It carries every fact in the rows next to it, which is why those
              rows stay: the image is the confirmation, the text is the record,
              and the text is the half a screen reader can read. */}
          <div className="card openmic-intro" style={{ background: "var(--c-cream-deep)", marginBottom: 8 }}>
            <OpenMicFacts cfg={cfg} />
            {cfg.flyer && (
              <img
                src={cfg.flyer}
                alt={`${cfg.name || "Open Mic Night"} flyer. ${[cfg.date, cfg.time, cfg.venueName].filter(Boolean).join(", ")}. Musicians, singers, rappers, poets, dancers, comedians. ${cfg.audience || ""}`.trim()}
                loading="lazy"
                style={{
                  display: "block",
                  width: "100%",
                  // Small enough that the card stays a banner rather than a
                  // panel with a hole beside three lines of text. It is a cue
                  // ("yes, this is the poster you saw"), not the place anyone
                  // reads the details -- those are the rows to its left.
                  maxWidth: 200,
                  height: "auto",
                  borderRadius: 10,
                  border: "2px solid var(--c-ink)",
                  justifySelf: "end",
                }}
              />
            )}
          </div>

          <form onSubmit={onSubmit}>
            <FormSection number="01" title="Who's performing?">
              <Row>
                <Field label="Your name" value={data.name} onChange={v => update("name", v)} required />
                <Field label="Your email" type="email" value={data.email} onChange={v => update("email", v)} required />
              </Row>
              <Row>
                <Field label="Phone (optional)" type="tel" value={data.phone} onChange={v => update("phone", v)} />
                <Field
                  label="Age group"
                  type="select"
                  options={["", ...OPEN_MIC_AGE_GROUPS]}
                  value={data.ageGroup}
                  onChange={v => update("ageGroup", v)}
                  required
                />
              </Row>
              <p style={{ fontSize: 13.5, color: "var(--c-ink-soft)", lineHeight: 1.55, margin: 0 }}>
                This one is for ages 13 and up. We ask so we know who needs a guardian to sign
                them in on the night.
              </p>
            </FormSection>

            <FormSection number="02" title="What are you doing?">
              <Row>
                <Field
                  label="What do you do?"
                  type="select"
                  options={["", ...OPEN_MIC_ACT_TYPES]}
                  value={data.actType}
                  onChange={v => update("actType", v)}
                  required
                />
                <Field
                  label="How long do you need?"
                  type="select"
                  options={["", ...OPEN_MIC_DURATIONS]}
                  value={data.duration}
                  onChange={v => update("duration", v)}
                  required
                />
              </Row>
              <Row>
                <Field label="Title of the piece (optional)" value={data.actTitle} onChange={v => update("actTitle", v)} />
                <Field
                  label="How many people are on stage?"
                  value={data.performers}
                  onChange={v => update("performers", v)}
                  placeholder="Just me"
                />
              </Row>
            </FormSection>

            <FormSection number="03" title="Anything we need to set up?">
              <Field
                label="Sound, stage or accessibility needs (optional)"
                type="textarea"
                value={data.needs}
                onChange={v => update("needs", v)}
                placeholder="Mic on a stand, a stool, a backing track on my phone, a ramp to the stage…"
              />
              <Field
                label="Anything else you'd like us to know (optional)"
                type="textarea"
                value={data.notes}
                onChange={v => update("notes", v)}
              />
            </FormSection>

            {error && (
              <div
                role="alert"
                style={{ marginTop: 24, padding: 14, background: "#FBE4DC", border: "2px solid var(--c-primary)", borderRadius: 12, fontSize: 14, color: "var(--c-ink)" }}
              >
                {error}
              </div>
            )}

            <div
              className="card"
              style={{ background: "var(--c-cream-deep)", marginTop: 32, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}
            >
              <div>
                <div className="display" style={{ fontSize: 20 }}>That's everything.</div>
                <div style={{ fontSize: 13, color: "var(--c-ink-soft)" }}>
                  We'll email you a slot and a call time before the night.
                </div>
              </div>
              <button type="submit" className="btn" disabled={submitting}>
                {submitting ? "Sending…" : "Put me on the list"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

/* The date, time, place and price, in one block, read straight off the config.
   Repeated on the confirmation because the thing a performer needs after
   signing up is exactly the thing they needed before it: when to turn up. */
function OpenMicFacts({ cfg }) {
  const rows = [
    ["When", [cfg.date, cfg.time].filter(Boolean).join(" · ")],
    ["Where", cfg.venueName],
    // The poster says AGES 13+ and says nothing about a price, so neither does
    // this. The row it replaced read "Free", which nobody had told us.
    ["Who", cfg.audience],
  ].filter(([, value]) => value);

  if (!rows.length) return null;

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: "flex", gap: 14, alignItems: "baseline", flexWrap: "wrap" }}>
          <div
            className="eyebrow"
            style={{ minWidth: 62, margin: 0, color: "var(--c-ink-soft)" }}
          >
            {label}
          </div>
          <div style={{ fontSize: 15.5, lineHeight: 1.5 }}>{value}</div>
        </div>
      ))}
      {cfg.venue && (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cfg.venue)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontFamily: "var(--f-mono)", fontSize: 12, letterSpacing: "0.06em", color: "var(--c-primary)" }}
        >
          View on map
        </a>
      )}
    </div>
  );
}

Object.assign(window, { OpenMicPage });
